import { useLayoutEffect, useState } from "react";
import { RotateCwIcon } from "lucide-react";

import { CurrencyEnum } from "@/__generated__/graphql";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { CommandCenterListFooter } from "@/layouts/AppLayout/CommandCenter/CommandCenterListFooter";
import { State } from "@/state";
import { Skeleton } from "@/components/ui/skeleton";
import { Typography } from "@/components/ui/typography";
import { Spinner } from "@/components/ui/spinner";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const COMMAND_ID = "read-document-again";

export type ReadDocumentAgainContextType = {
  /** The document's node id. */
  id: string;
  identifier: string;
  name?: string | null;
  /** The currency it is filed in now: kept, unless another is picked. */
  currency?: string | null;
};

/**
 * A document read again from its file, its lines and all replaced by what the
 * file says - in the currency it is in, or another picked to put a mistake
 * right, or "As printed" for what the file says. Once anything is booked
 * against it the books would say what it no longer does, so it is not read
 * again; the server refuses it too.
 */
function Component({ context }: { context?: ReadDocumentAgainContextType }) {
  const clearCommandStack = State((store) => store.clearCommandStack);
  const setSnackbarMessage = State((store) => store.setSnackbarMessage);
  const [currency, setCurrency] = useState<string | null>(
    context?.currency ?? null,
  );
  const name = context?.name || "This document";

  const { data, loading } = useBackendQuery(Queries.DOCUMENT_IS_BOOKED_QUERY, {
    variables: { id: context?.id ?? "" },
    skip: !context?.id,
    // Booked a moment ago in another tab, it is refused all the same: asked
    // afresh, not from what the cache last heard.
    fetchPolicy: "network-only",
  });
  const isBooked =
    data?.node?.__typename === "DocumentObject" ? data.node.isBooked : null;

  const { mutate: readAgain, loading: isAsking } = useBackendMutation({
    mutation: Mutations.READ_DOCUMENT_AGAIN,
    callbacks: {
      onSuccess: () => {
        setSnackbarMessage(
          `Reading ${name} again - what it says changes once it is read.`,
        );
        clearCommandStack();
      },
    },
  });

  const isReady = !loading && isBooked === false && !isAsking;
  return (
    <StickyLayout
      fillHeight={false}
      stickyBottomChildren={
        <CommandCenterListFooter
          buttons={[
            {
              variant: "default",
              size: "sm",
              children: <>{isAsking && <Spinner />}Read again</>,
              "data-testid": "read-document-again-submit",
              hotkey: "enter",
              disabled: isAsking || !isReady,

              onClick: () => {
                if (!isReady || !context?.identifier) return;
                readAgain({
                  variables: {
                    input: {
                      identifier: context.identifier,
                      amountCurrency: (currency as CurrencyEnum | null) ?? null,
                    },
                  },
                });
              },
            },
          ]}
        />
      }
    >
      <div className="flex flex-col gap-3 p-4">
        <Typography level="body-md" data-testid="read-document-again-says">
          <Skeleton loading={loading} variant="inline">
            {isBooked
              ? `Something is booked against ${name} - a payment of it, or a journal entry - so it can't be read again: the books would say what it no longer does.`
              : `${name} is read again from its file, and what the file says replaces what it says now - its lines included, with any account you gave one.`}
          </Skeleton>
        </Typography>
        {!isBooked && (
          // The same choice as on importing it: the currency it is filed in.
          <div className="flex items-center justify-between gap-2">
            <Typography level="body-sm" className="text-muted-foreground">
              The currency it is in
            </Typography>
            <Select
              // "As printed" is an item of its own: Radix's Select takes no empty value.
              value={currency ?? "printed"}
              onValueChange={(value) =>
                setCurrency(value === "printed" ? null : value)
              }
            >
              <SelectTrigger
                size="sm"
                aria-label="The currency it is in"
                data-testid="read-document-again-currency"
                className="min-w-28"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="printed">As printed</SelectItem>
                {Object.values(CurrencyEnum).map((code) => (
                  <SelectItem
                    key={code}
                    value={code}
                    data-testid={`read-document-again-currency-${code}`}
                  >
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </StickyLayout>
  );
}

export function ReadDocumentAgainCommand() {
  const registerCommandDefinition = State(
    (state) => state.registerCommandDefinition,
  );
  const unregisterCommandDefinition = State(
    (state) => state.unregisterCommandDefinition,
  );

  useLayoutEffect(() => {
    registerCommandDefinition({
      id: COMMAND_ID,
      title: "Read again",
      getLabel: () => "Read again",
      description: "Read a document again from its file",
      component: ({ context }) => (
        <Component context={context as ReadDocumentAgainContextType} />
      ),
      ReactIcon: RotateCwIcon,
      isHidden: true,
    });
    return () => unregisterCommandDefinition(COMMAND_ID);
  }, []);

  return null;
}
